import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import useAdminLoad from "@/hooks/useAdminLoad";
import { LoadState, PageHeader, Panel } from "@/components/admin/AdminUI";
import { api } from "@/lib/api";
import ListEditor from "@/components/admin/ListEditor";
import PrelaunchChecklist from "@/components/admin/PrelaunchChecklist";
import ReviewerAccessCard, { REVIEWER_KEYS } from "@/components/admin/ReviewerAccessCard";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SETTINGS_GROUPS, SETTING_META, settingMeta } from "@/lib/constants";

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

const NUMERIC = { int: /^-?\d+$/, float: /^-?\d+(\.\d+)?$/, number: /^-?\d+(\.\d+)?$/ };
const KNOWN_ORDER = Object.keys(SETTING_META);

// Draft values are strings; list settings are kept as JSON-encoded arrays.
const serialize = (s, v) => (s.type === "list" ? JSON.stringify(v || []) : String(v));
const parseList = (raw) => { try { const v = JSON.parse(raw); return Array.isArray(v) ? v : []; } catch { return []; } };

// Enum settings carry their options in `options` (or `min`, as the API sends today).
const enumOptions = (s) => (Array.isArray(s.options) ? s.options : Array.isArray(s.min) ? s.min : []);

function check(s, raw) {
  if (s.type === "enum") return enumOptions(s).includes(raw) ? null : "Choose an option.";
  if (s.type === "list") return s.max != null && parseList(raw).length > s.max ? `At most ${s.max} items.` : null;
  if (s.type === "time") return TIME_RE.test(raw) ? null : "Use 24-hour time, like 23:00.";
  if (s.type === "bool") return null;
  if (s.type === "url") return !String(raw).trim() || /^https:\/\/\S+$/.test(String(raw).trim()) ? null : "Use a full https:// link, or leave empty to hide it.";
  if (!NUMERIC[s.type]) return String(raw).trim() ? null : "Enter a value.";
  if (!NUMERIC[s.type].test(String(raw).trim())) return s.type === "int" ? "Enter a whole number." : "Enter a number.";
  const n = +raw;
  if ((s.min != null && n < s.min) || (s.max != null && n > s.max)) return `Between ${s.min ?? "−∞"} and ${s.max ?? "∞"}.`;
  return null;
}

const toValue = (s, raw) => (s.type === "list" ? parseList(raw) : s.type === "int" ? parseInt(raw, 10) : NUMERIC[s.type] ? parseFloat(raw) : s.type === "bool" ? raw === "true" : raw);
const rangeText = (s) => (s.type === "enum" ? `One of: ${enumOptions(s).join(", ")}` : s.type === "list" ? `List${s.max != null ? `, up to ${s.max}` : ""}` : s.type === "time" ? "HH:MM" : NUMERIC[s.type] ? (s.min != null || s.max != null ? `${s.min ?? "−∞"}–${s.max ?? "∞"}` : "number") : s.type);

// Group every setting the API returns; known keys keep their curated order, unknown keys follow.
function buildGroups(items) {
  const rank = (k) => (KNOWN_ORDER.includes(k) ? KNOWN_ORDER.indexOf(k) : KNOWN_ORDER.length);
  const sorted = [...items].sort((a, b) => rank(a.key) - rank(b.key) || a.key.localeCompare(b.key));
  const metas = sorted.map((s) => ({ key: s.key, ...settingMeta(s) }));
  // Known groups first in their curated order; any new API group is appended (before "Other").
  const extra = [...new Set(metas.map((m) => m.group))].filter((t) => !SETTINGS_GROUPS.some((g) => g.title === t)).map((title) => ({ title }));
  const order = [...SETTINGS_GROUPS.filter((g) => g.title !== "Other"), ...extra, SETTINGS_GROUPS.find((g) => g.title === "Other")];
  return order.map((g) => ({ ...g, items: metas.filter((m) => m.group === g.title) })).filter((g) => g.items.length);
}

function Field({ meta, s, value, onChange, error }) {
  const id = `setting-${s.key}`;
  const changed = value !== serialize(s, s.value);
  const isList = s.type === "list";
  const def = serialize(s, s.default);
  return (
    <div className={`grid gap-2 py-4 sm:items-start sm:gap-6 ${isList ? "" : "sm:grid-cols-[1fr_220px]"}`} data-testid={`setting-row-${s.key}`}>
      <div>
        <label htmlFor={id} className="text-sm font-medium">{meta.label}</label>
        <p className="cw-muted mt-0.5 text-xs">
          {rangeText(s)}{isList ? "" : ` · default ${String(s.default)}`}{isList && changed && <span className="cw-brand-deep ml-2">Unsaved</span>}
          {s.default != null && value !== def && <button type="button" onClick={() => onChange(def)} className="cw-brand-deep ml-2 rounded underline-offset-4 hover:underline" data-testid={`setting-reset-${s.key}`}>Use default</button>}
        </p>
        {meta.warning && s.type !== "enum" && <p className="cw-soft mt-1 max-w-xl text-xs leading-5" data-testid={`setting-warning-${s.key}`}>{meta.warning}</p>}
      </div>
      {isList ? (
        <ListEditor id={id} settingKey={s.key} items={parseList(value)} max={s.max} onChange={(arr) => onChange(JSON.stringify(arr))} />
      ) : s.type === "bool" ? (
        <div className="flex h-11 items-center gap-3">
          <Switch id={id} checked={value === "true"} onCheckedChange={(v) => onChange(String(v))} data-testid={`setting-switch-${s.key}`} />
          <span className="cw-soft text-sm">{value === "true" ? "On" : "Off"}</span>
        </div>
      ) : s.type === "enum" ? (
        <div>
          <Select value={value} onValueChange={onChange}>
            <SelectTrigger id={id} className={`h-11 rounded-xl bg-white capitalize ${changed ? "border-primary" : ""}`} data-testid={`setting-select-${s.key}`}><SelectValue /></SelectTrigger>
            <SelectContent>{enumOptions(s).map((o) => <SelectItem key={o} value={o} className="capitalize" data-testid={`setting-option-${s.key}-${o}`}>{o}</SelectItem>)}</SelectContent>
          </Select>
          {meta.warning && <p className="cw-warning mt-2 text-xs leading-5" data-testid={`setting-warning-${s.key}`}>{meta.warning}</p>}
        </div>
      ) : (
      <div>
        <div className="relative">
          <Input id={id} type={s.type === "time" ? "time" : NUMERIC[s.type] ? "number" : "text"} inputMode={s.type === "int" ? "numeric" : s.type === "float" ? "decimal" : undefined}
            min={s.min ?? undefined} max={s.max ?? undefined} step={s.type === "int" ? 1 : "any"}
            value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={!!error} aria-describedby={`${id}-err`}
            className={`h-11 rounded-xl bg-white pr-20 tabular-nums ${changed ? "border-primary" : ""}`} data-testid={`setting-input-${s.key}`} />
          {meta.unit && <span className="cw-muted pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs">{meta.unit}</span>}
        </div>
        {error && <p id={`${id}-err`} className="cw-error mt-1.5 text-xs" role="alert" data-testid={`setting-error-${s.key}`}>{error}</p>}
      </div>
      )}
      {isList && error && <p className="cw-error text-xs" role="alert" data-testid={`setting-error-${s.key}`}>{error}</p>}
    </div>
  );
}

export default function Settings() {
  const { data, error, loading, reload, setData } = useAdminLoad("/admin/settings");
  const byKey = useMemo(() => Object.fromEntries((data?.items || []).map((s) => [s.key, s])), [data]);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [serverError, setServerError] = useState(null);
  useEffect(() => { setDraft(Object.fromEntries((data?.items || []).map((s) => [s.key, serialize(s, s.value)]))); }, [data]);

  const errors = Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, byKey[k] ? check(byKey[k], v) : null]).filter(([, e]) => e));
  const changed = Object.keys(draft).filter((k) => byKey[k] && draft[k] !== serialize(byKey[k], byKey[k].value));
  // The reviewer settings are edited in the Reviewer access card, not in the generic groups.
  const groups = useMemo(() => buildGroups((data?.items || []).filter((s) => !REVIEWER_KEYS.includes(s.key))), [data]);

  async function save() {
    setBusy(true);
    setServerError(null);
    try {
      const values = Object.fromEntries(changed.map((k) => [k, toValue(byKey[k], draft[k])]));
      const res = await api("/admin/settings", { method: "PATCH", body: { values } });
      setData(res);
      setSavedAt(new Date());
      toast.success(`Saved ${changed.length} setting${changed.length === 1 ? "" : "s"}.`);
    } catch (e) {
      setServerError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="admin-settings-page" className="pb-24">
      <PageHeader title="Settings" description="Tune limits and timings across the app. Changes apply right away." />
      <div className="mb-6"><PrelaunchChecklist /></div>
      <div className="mb-6"><ReviewerAccessCard /></div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <div className="space-y-6">
          {groups.map((g) => (
            <Panel key={g.title} className="px-6 py-5" data-testid={`settings-group-${g.title.toLowerCase().replace(/\s/g, "-")}`}>
              <h2 className="font-semibold">{g.title}</h2>
              {g.hint && <p className="cw-soft mt-1 text-sm">{g.hint}</p>}
              <div className="mt-2 divide-y divide-border">
                {g.items.filter((m) => byKey[m.key]).map((m) => (
                  <Field key={m.key} meta={m} s={byKey[m.key]} value={draft[m.key] ?? ""} error={errors[m.key]}
                    onChange={(v) => { setDraft((d) => ({ ...d, [m.key]: v })); setSavedAt(null); }} />
                ))}
              </div>
            </Panel>
          ))}
        </div>
      </LoadState>
      {data && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-white/90 backdrop-blur-md lg:left-64">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-3 sm:px-8">
            <p className="text-sm" data-testid="settings-save-status" role="status">
              {serverError ? <span className="cw-error">{serverError}</span>
                : Object.keys(errors).length ? <span className="cw-error">Fix {Object.keys(errors).length} field{Object.keys(errors).length === 1 ? "" : "s"} to save.</span>
                  : changed.length ? <span className="cw-soft">{changed.length} unsaved change{changed.length === 1 ? "" : "s"}</span>
                    : savedAt ? <span className="cw-success inline-flex items-center gap-1.5" data-testid="settings-saved-message"><CheckCircle size={16} weight="fill" />All changes saved</span>
                      : <span className="cw-muted">No changes</span>}
            </p>
            <div className="flex gap-2">
              {changed.length > 0 && <Button variant="ghost" onClick={() => setDraft(Object.fromEntries(data.items.map((s) => [s.key, serialize(s, s.value)])))} data-testid="settings-discard-button">Discard</Button>}
              <Button onClick={save} disabled={busy || !changed.length || Object.keys(errors).length > 0} data-testid="settings-save-button">{busy ? "Saving…" : "Save changes"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
